// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Rafael Pereira Satyro

import { useSearchParams } from 'react-router-dom';
import type { Client } from '../../../types/client';
import { MaxFundingScenariosList } from './maxfunding/MaxFundingScenariosList';
import { MaxFundingScenarioPage } from './maxfunding/MaxFundingScenarioPage';

type RelationshipToolsTabProps = Readonly<{
  profileId: string;
  brokerageId: string;
  clients: Client[];
}>;

export function RelationshipToolsTab({ profileId, clients }: RelationshipToolsTabProps) {
  const [searchParams] = useSearchParams();
  const tool = searchParams.get('tool');
  const view = searchParams.get('view');
  const scenarioId = searchParams.get('scenarioId');

  if (tool === 'max-funding') {
    // Existing persisted scenario — fetch by id.
    if (view === 'scenario' && scenarioId) {
      return (
        <MaxFundingScenarioPage
          key={`scenario-${scenarioId}`}
          profileId={profileId}
          scenarioId={scenarioId}
          clientId={null}
          clients={clients}
        />
      );
    }
    // New in-memory scenario — no backend row yet, clientId identifies the future owner.
    const clientId = searchParams.get('clientId');
    if (view === 'new' && clientId) {
      return (
        <MaxFundingScenarioPage
          key={`new-${clientId}`}
          profileId={profileId}
          scenarioId={null}
          clientId={clientId}
          clients={clients}
        />
      );
    }
    return <MaxFundingScenariosList profileId={profileId} clients={clients} />;
  }

  // No tool selected yet — show a prompt to pick from the Tools menu.
  return (
    <div
      className="flex items-center justify-center gap-[10px] self-stretch rounded-card bg-lilac-06 px-4 py-10"
      data-testid="tools-tab-placeholder"
    >
      <span className="text-body-02 text-black font-normal leading-normal">
        Select a tool from the Tools menu above to get started.
      </span>
    </div>
  );
}
